import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { className, label, hint, error, iconLeft, iconRight, containerClassName, id, ...rest },
    ref,
  ) => {
    const inputId = id ?? `input-${rest.name ?? Math.random().toString(36).slice(2, 8)}`;
    return (
      <div className={cn('flex flex-col gap-1.5', containerClassName)}>
        {label && (
          <label htmlFor={inputId} className="text-caption text-ink-muted font-medium">
            {label}
          </label>
        )}
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg border border-line bg-white px-3',
            'focus-within:border-brand-blue focus-within:shadow-focus',
            error && 'border-status-error focus-within:border-status-error',
          )}
        >
          {iconLeft && <span className="text-ink-hint">{iconLeft}</span>}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'h-11 w-full bg-transparent text-body text-ink outline-none placeholder:text-ink-hint',
              'disabled:cursor-not-allowed disabled:opacity-50',
              className,
            )}
            {...rest}
          />
          {iconRight && <span className="text-ink-hint">{iconRight}</span>}
        </div>
        {error ? (
          <p className="text-caption text-status-error">{error}</p>
        ) : hint ? (
          <p className="text-caption text-ink-muted">{hint}</p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
